import ts, { ScriptTarget } from 'typescript';
import{ DEBUG_EVALUATE_EXPRESSIONS } from '@/configuration/client';
import * as Sentry from '@sentry/nextjs';
import evaluators from './evaluators';
import { EvaluateResponse } from './types';
import { BaseEvaluateExpressionError } from './errors';

const log = DEBUG_EVALUATE_EXPRESSIONS
  ? console
  : {
    error: () => undefined,
    info: () => undefined,
    log: () => undefined,
  };

const EMPTY_PROFILE = {
  evaluateTime: 0,
  parseTime: 0,
  runs: 0,
};

let countsById: Record<string, number> = {};
let isProfileEnabled = false;
let thisProfile = { ...EMPTY_PROFILE };
export const enableProfiling = (): void => {
  isProfileEnabled = true;
  thisProfile = { ...EMPTY_PROFILE };
  countsById = {};
};
export const disableProfiling = (): void => {
  isProfileEnabled = false;
};

type ProfileData = {
  summary: typeof EMPTY_PROFILE;
  countsById: Record<string, number>;
};
export const getProfilingData = (): ProfileData => ({
  countsById,
  summary: thisProfile,
});

// Use https://github.com/wessberg/ts-evaluator for hints about adding evaluators
const evaluateExpression = ({ expression: initialExpression, variables, id }: {
  expression: string;
  id?: string;
  variables: Record<string, unknown>;
}): Exclude<EvaluateResponse, BaseEvaluateExpressionError> | Array<EvaluateResponse> => {
  let previousTime: number = 0;
  if (isProfileEnabled) {
    thisProfile.runs += 1;
    previousTime = Date.now();
    if (id) {
      if (countsById[id] === undefined) {
        countsById[id] = 0;
      }
      countsById[id] += 1;
    }
  }
  const expression = `var ___ = ${initialExpression}`;

  const program = ts.createSourceFile('evaluate-expression.ts', expression, ScriptTarget.ES2022);
  const node = (program.statements[0] as ts.VariableStatement).declarationList.declarations[0].initializer;
  if (isProfileEnabled) {
    const thisTime = Date.now();
    thisProfile.parseTime += (thisTime - previousTime);
    previousTime = thisTime;
  }

  if (node) {
    const response = evaluators.expression({
      evaluators,
      log,
      node,
      variables,
    });
    if (isProfileEnabled) {
      const thisTime = Date.now();
      thisProfile.evaluateTime += (thisTime - previousTime);
    }
    if (response instanceof Error) {
      console.error(response, initialExpression, 'evaluateExpression error');
      Sentry.captureException(response, {
        extra: { expression: initialExpression, variables },
      });
      return undefined;
    }

    return response;
  }

  const error = new Error('No node available in evaluateExpression');
  console.error(error, initialExpression, 'evaluateExpression error');
  Sentry.captureException(error, {
    extra: { initialExpression },
  });
  return undefined;
};

export default evaluateExpression;
